import { useMemo } from 'react';
import { ExternalLink, FileText, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DmsDocumentPicker } from '@/features/attachments/components/DmsDocumentPicker';
import {
  useReceivingDmsLinks,
  useAddReceivingDmsLink,
  useUpdateReceivingDmsLink,
  useDeleteReceivingDmsLink,
} from '../../hooks/useReceivingDmsLinks';
import type { ReceivingOrder, ReceivingDmsLinkKind } from '../../types';

const KIND_OPTIONS: { value: ReceivingDmsLinkKind; label: string }[] = [
  { value: 'supplier_invoice', label: 'Supplier Invoice' },
  { value: 'packing_slip', label: 'Packing Slip' },
  { value: 'receiving_report', label: 'Receiving Report' },
  { value: 'certificate_of_conformance', label: 'Certificate of Conformance' },
  { value: 'other', label: 'Other' },
];

interface Props {
  order: ReceivingOrder;
}

export function ReceivingDocumentsTab({ order }: Props) {
  const { data: links = [], isLoading } = useReceivingDmsLinks(order.id);
  const addMutation = useAddReceivingDmsLink();
  const patchMutation = useUpdateReceivingDmsLink();
  const deleteMutation = useDeleteReceivingDmsLink();

  const excludeIds = useMemo(
    () => new Set(links.map((l) => l.dmsDocumentId)),
    [links],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Link supplier invoices, packing slips, receiving reports, and other DMS documents to this order.
        </p>
        <DmsDocumentPicker
          excludeIds={excludeIds}
          onSelect={(doc) =>
            addMutation.mutate({
              receivingOrderId: order.id,
              dmsDocumentId: doc.id,
              kind: 'other',
              label: doc.currentVersion?.originalName ?? null,
            })
          }
          trigger={
            <Button size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-1" /> Link document
            </Button>
          }
        />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && links.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No documents linked yet. Click <strong>Link document</strong> to attach a supplier
          invoice, packing slip, or other reference from DMS.
        </div>
      )}

      {!isLoading && links.length > 0 && (
        <div className="rounded-lg border divide-y">
          {links.map((link) => (
            <div key={link.id} className="flex items-center gap-3 px-3 py-2.5">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{link.label || link.dmsDocumentId}</p>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {link.dmsDocumentId}
                </p>
              </div>
              <Select
                value={link.kind}
                onValueChange={(v) => {
                  if (v === '') return;
                  patchMutation.mutate({
                    receivingOrderId: order.id,
                    linkId: link.id,
                    patch: { kind: v as ReceivingDmsLinkKind },
                  });
                }}
              >
                <SelectTrigger className="w-[200px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <a
                href={`/documents/${link.dmsDocumentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                title="Open in DMS"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => {
                  if (confirm(`Unlink "${link.label || link.dmsDocumentId}"?`)) {
                    deleteMutation.mutate({ receivingOrderId: order.id, linkId: link.id });
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
